(() => {
  if (window.MarmeladeFlyout) return;
  const stack = [];
  const headerSelector = '[data-marmelade-header]';
  const focusSelector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  class Flyout {
    constructor(dialog, { panel, handle, dismiss = handle, backdrop, canClose = () => true,
      requestClose, initialFocus, onClose, layout = 'picker', scroll } = {}) {
      if (!dialog || !panel || !handle || !dismiss) throw new Error('[Flyout] Dialog, panel and handle are required.');
      Object.assign(this, { dialog, panel, handle, dismiss, backdrop, canClose, initialFocus, onClose, scroll });
      this.requestClose = requestClose || (options => this.close(options));
      this.native = dialog.tagName === 'DIALOG';
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      dialog.classList.add('marmelade-flyout', `marmelade-flyout--${layout}`);
      dialog.setAttribute('aria-modal', 'false'); // The site header remains available.
      panel.classList.add('marmelade-flyout-panel');
      handle.classList.add('marmelade-flyout-drag');
      dismiss.classList.add('marmelade-flyout-dismiss');
      backdrop?.classList.add('marmelade-flyout-backdrop');
      dismiss.addEventListener('click', event => {
        if (this.suppressClick) { event.preventDefault(); this.suppressClick = false; return; }
        this.dismissFlyout();
      }, options);
      backdrop?.addEventListener('click', () => this.dismissFlyout(), options);
      dialog.addEventListener('click', event => { if (event.target === dialog) this.dismissFlyout(); }, options);
      dialog.addEventListener('cancel', event => { event.preventDefault(); this.dismissFlyout(); }, options);
      dialog.addEventListener('close', () => { if (!this.isOpen && stack.includes(this)) this.cleanup(); }, options);
      document.addEventListener('keydown', event => {
        if (stack.at(-1) !== this || !this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault(); event.stopImmediatePropagation(); this.dismissFlyout();
        } else if (event.key === 'Tab') this.trapFocus(event);
      }, options);
      handle.addEventListener('pointerdown', event => {
        if (event.button !== 0 || !this.canClose() || this.closing || !this.isOpen) return;
        event.preventDefault();
        this.cancelAnimations();
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, delta: 0, moved: false,
          startedOnDismiss: dismiss.contains(event.target) };
        this.suppressClick = false;
        handle.setPointerCapture(event.pointerId);
      }, options);
      handle.addEventListener('pointermove', event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        this.drag.delta = Math.max(0, event.clientY - this.drag.y);
        this.drag.moved ||= Math.abs(event.clientY - this.drag.y) > 6 || Math.abs(event.clientX - this.drag.x) > 6;
        this.suppressClick = this.drag.moved;
        panel.style.transform = `translateY(${this.drag.delta}px)`;
      }, options);
      const finish = event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const { delta, moved, startedOnDismiss } = this.drag;
        this.drag = null;
        if (event.type !== 'pointerup') this.suppressClick = true;
        if (event.type === 'pointerup' && ((startedOnDismiss && !moved) || delta >= Math.max(72, panel.offsetHeight * .15))) {
          this.suppressClick = true;
          this.dismissFlyout();
        }
        else this.resetPosition();
      };
      handle.addEventListener('pointerup', finish, options);
      handle.addEventListener('pointercancel', finish, options);
      handle.addEventListener('lostpointercapture', finish, options);
      // Media sheets also dismiss on a downward swipe from the top of their scroll area.
      if (scroll) {
        scroll.addEventListener('touchstart', event => {
          this.touch = event.touches.length === 1 && scroll.scrollTop <= 0 && !event.target.closest('button, a')
            ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
        }, { ...options, passive: true });
        scroll.addEventListener('touchend', event => {
          if (this.touch && event.changedTouches.length) {
            const dx = event.changedTouches[0].clientX - this.touch.x;
            const dy = event.changedTouches[0].clientY - this.touch.y;
            if (dy > 75 && Math.abs(dx) < 60) this.dismissFlyout();
          }
          this.touch = null;
        }, options);
        scroll.addEventListener('touchcancel', () => { this.touch = null; }, options);
      }
    }
    static dismissForHeader() {
      const openSheets = [...stack].reverse();
      if (openSheets.some(sheet => !sheet.canClose())) return false;
      openSheets.forEach(sheet => sheet.dismissFlyout({ restoreFocus: false, immediate: true }));
      return true;
    }
    get isOpen() { return this.native ? this.dialog.open : !this.dialog.hidden; }
    dismissFlyout(options) { if (this.isOpen && this.canClose() && (!this.closing || options?.immediate)) this.requestClose(options); }
    cancelAnimations() {
      this.animations?.forEach(animation => animation.cancel());
      this.panel.getAnimations().forEach(animation => animation.cancel());
    }
    resetPosition() {
      this.cancelAnimations();
      this.animations = [this.panel.animate([
        { transform: getComputedStyle(this.panel).transform }, { transform: 'translateY(0)' }
      ], { duration: reduced() ? 0 : 180, easing: 'ease-out' })];
      this.panel.style.removeProperty('transform');
    }
    focusable() {
      return [...this.panel.querySelectorAll(focusSelector),
        ...document.querySelectorAll(`${headerSelector} ${focusSelector.split(', ').join(`, ${headerSelector} `)}`)]
        .filter(element => !element.closest('[hidden], [inert]') && element.getClientRects().length > 0);
    }
    trapFocus(event) {
      const elements = this.focusable();
      const first = elements[0], last = elements.at(-1);
      if (!first) { event.preventDefault(); this.dismiss.focus({ preventScroll: true }); return; }
      const outside = !elements.includes(document.activeElement);
      if (event.shiftKey && (outside || document.activeElement === first)) { event.preventDefault(); last.focus({ preventScroll: true }); }
      else if (!event.shiftKey && (outside || document.activeElement === last)) { event.preventDefault(); first.focus({ preventScroll: true }); }
    }
    open(trigger = document.activeElement) {
      if (this.isOpen) return;
      this.trigger = trigger;
      this.restoreFocus = true;
      this.suppressClick = false;
      this.cancelAnimations();
      this.panel.style.removeProperty('transform');
      // showModal() makes the header inert and places its backdrop above it.
      if (this.native) this.dialog.show();
      else this.dialog.hidden = false;
      this.isolation = [];
      const header = document.querySelector(headerSelector);
      let current = this.dialog;
      while (current && current !== document.body) {
        const parent = current.parentElement;
        if (!parent) break;
        [...parent.children].forEach(element => {
          if (element === current || (header && element.contains(header))) return;
          // Closed sibling sheets may be opened from this flyout (e.g. Quick Add → device picker).
          if (element.classList.contains('marmelade-flyout') && !(element.tagName === 'DIALOG' ? element.open : !element.hidden)) return;
          this.isolation.push({ element, inert: element.inert, ariaHidden: element.getAttribute('aria-hidden') });
          element.inert = true;
          element.setAttribute('aria-hidden', 'true');
        });
        current = parent;
      }
      this.dialog.style.setProperty('--marmelade-flyout-level', 10020 + stack.length);
      stack.push(this);
      document.documentElement.classList.add('marmelade-flyout-open');
      trigger?.setAttribute('aria-expanded', 'true');
      (this.initialFocus?.() || this.dismiss).focus({ preventScroll: true });
      [this.dialog, this.panel, this.scroll].filter(Boolean).forEach(element => { element.scrollTop = 0; });
      const desktop = this.dialog.classList.contains('marmelade-flyout--picker') && window.matchMedia('(min-width: 1024px)').matches;
      this.animations = [this.panel.animate(desktop ? [
        { opacity: 0, transform: 'translateY(14px) scale(.985)' }, { opacity: 1, transform: 'translateY(0) scale(1)' }
      ] : [{ transform: 'translateY(100dvh)' }, { transform: 'translateY(0)' }],
      { duration: reduced() ? 0 : desktop ? 200 : 280, easing: 'cubic-bezier(.22,.8,.25,1)' })];
    }
    close({ restoreFocus = true, force = false, immediate = false } = {}) {
      if (!this.isOpen) return Promise.resolve();
      if (this.closing && !immediate) return this.closePromise;
      if (!force && !this.canClose()) return Promise.resolve();
      this.restoreFocus = restoreFocus;
      if (immediate) { this.finishClose(); return Promise.resolve(); }
      const transform = getComputedStyle(this.panel).transform;
      this.cancelAnimations();
      this.closing = true;
      this.dialog.classList.add('is-closing');
      this.animations = [this.panel.animate([
        { transform }, { transform: 'translateY(100dvh)' }
      ], { duration: reduced() ? 0 : 220, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' })];
      if (this.backdrop) this.animations.push(this.backdrop.animate([{ opacity: 1 }, { opacity: 0 }], { duration: reduced() ? 0 : 220, fill: 'forwards' }));
      this.closePromise = new Promise(resolve => {
        this.resolveClose = resolve;
        this.closeTimer = window.setTimeout(() => {
          this.finishClose();
        }, reduced() ? 0 : 220);
      });
      return this.closePromise;
    }
    finishClose() {
      if (this.native) this.dialog.close(); else this.dialog.hidden = true;
      if (stack.includes(this)) this.cleanup();
    }
    cleanup() {
      window.clearTimeout(this.closeTimer);
      this.cancelAnimations();
      this.panel.style.removeProperty('transform');
      this.dialog.classList.remove('is-closing', 'is-loading');
      this.drag = this.touch = null;
      this.closing = false;
      this.isolation?.forEach(({ element, inert, ariaHidden }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden'); else element.setAttribute('aria-hidden', ariaHidden);
      });
      this.isolation = [];
      const index = stack.indexOf(this);
      if (index >= 0) stack.splice(index, 1);
      if (!stack.length) document.documentElement.classList.remove('marmelade-flyout-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (this.restoreFocus && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      this.resolveClose?.();
      this.resolveClose = this.closePromise = null;
      this.onClose?.();
    }
    destroy() {
      this.restoreFocus = false;
      if (this.isOpen) this.finishClose(); else this.cleanup();
      this.events.abort();
    }
  }
  document.addEventListener('click', event => {
    if (!event.target.closest(`${headerSelector} a, ${headerSelector} button`)) return;
    if (!Flyout.dismissForHeader()) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  window.MarmeladeFlyout = Flyout;
})();
